import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Mail, Plus, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientAssignments } from "@/components/ClientAssignments";
import { ClientNotes } from "@/components/ClientNotes";
import { ClientAiSummary } from "@/components/ClientAiSummary";
import { ClientSharedReflections } from "@/components/ClientSharedReflections";
import { DigestPreviewButton } from "@/components/DigestPreviewButton";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

const InviteForm = ({ kind, onDone, disabled }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/clients", { name, email, kind });
      if (r.data.email_sent) toast.success(`Invitation emailed to ${email}`);
      else toast.warning("Email could not be sent — copy the invite link from the list below");
      setName(""); setEmail("");
      onDone(r.data);
    } catch (err) { toast.error(errorMessage(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid={`invite-${kind}-form`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="h-11 rounded-full bg-white sm:max-w-[200px]" data-testid={`invite-${kind}-name-input`} />
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="h-11 rounded-full bg-white" data-testid={`invite-${kind}-email-input`} />
      <Button type="submit" disabled={loading || disabled} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`invite-${kind}-submit-button`}><UserPlus className="w-4 h-4" /> {loading ? "Sending…" : kind === "client" ? "Invite client" : "Add clinician"}</Button>
    </form>
  );
};

export default function Clients() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [links, setLinks] = useState({});
  const [tutorials, setTutorials] = useState([]);

  const load = () => api.get("/clients").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); api.get("/tutorials").then((r) => setTutorials(r.data)).catch(() => {}); }, []);

  const onInvited = (res) => { if (res.invite_link) setLinks((l) => ({ ...l, [res.user.id]: res.invite_link })); load(); };
  const resend = async (u) => {
    try {
      const r = await api.post(`/clients/${u.id}/resend`);
      if (r.data.email_sent) toast.success("Invitation re-sent"); else { setLinks((l) => ({ ...l, [u.id]: r.data.invite_link })); toast.warning("Email unavailable — link shown below"); }
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? Their progress will be deleted.`)) return;
    try { await api.delete(`/clients/${u.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const copy = (link) => { navigator.clipboard?.writeText(link); toast.success("Link copied"); };

  if (!data) return <div className="max-w-6xl mx-auto px-4 py-12"><Skeleton className="h-64 rounded-3xl" /></div>;
  const inactive = !data.access.active;

  const Row = ({ u, showStats }) => (
    <li className="flex flex-wrap items-center gap-3 px-6 py-4" data-testid={`person-row-${u.id}`}>
      <span className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 grid place-items-center font-bold shrink-0">{(u.name || u.email)[0].toUpperCase()}</span>
      <div className="flex-1 min-w-[180px]">
        <p className="font-medium text-slate-900">{u.name || u.email}</p>
        <p className="text-xs text-slate-500">{u.email} · <span className={u.status === "invited" ? "text-amber-600 font-semibold" : "text-emerald-700 font-semibold"}>{u.status === "invited" ? "Invited — not yet joined" : "Active"}</span></p>
        {links[u.id] && <p className="text-xs text-indigo-700 mt-1 break-all flex items-center gap-1"><span className="truncate max-w-[320px]">{links[u.id]}</span><button type="button" onClick={() => copy(links[u.id])} className="shrink-0" aria-label="Copy link"><Copy className="w-3 h-3" /></button></p>}
      </div>
      {showStats && u.stats && (
        <div className="flex gap-5 text-center text-xs text-slate-500" data-testid={`person-stats-${u.id}`}>
          <div><p className="font-display text-lg font-bold text-slate-900">{u.stats.sessions}</p>sessions</div>
          <div><p className="font-display text-lg font-bold text-slate-900">{u.stats.minutes}</p>minutes</div>
          <div><p className="font-display text-lg font-bold text-slate-900">{u.stats.completed}</p>finished</div>
          <div><p className="font-display text-lg font-bold text-slate-900">{fmt(u.stats.last_active)}</p>last active</div>
        </div>
      )}
      <div className="flex gap-1">
        {u.status === "invited" && <Button size="icon" variant="ghost" onClick={() => resend(u)} aria-label="Resend invite" data-testid={`resend-${u.id}`}><Mail className="w-4 h-4" /></Button>}
        <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(u)} aria-label="Remove" data-testid={`remove-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
      {showStats && <ClientAssignments client={u} tutorials={tutorials} />}
      {showStats && <ClientNotes client={u} />}
      {showStats && <ClientAiSummary client={u} />}
      {showStats && <ClientSharedReflections client={u} />}
    </li>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16 space-y-10" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <p className="eyebrow">Clinician dashboard</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Clients</h1>
          <p className="text-slate-600 max-w-2xl">Invite clients by email. They set a password from a secure link and practise for free under your plan. You see practice totals and, if you opt in, a weekly AI summary of patterns — never their individual mood answers.</p>
        </div>
        <DigestPreviewButton />
      </div>

      {inactive && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3" data-testid="clients-inactive-banner">
          <span>Inviting requires an active subscription.</span>
          {!user?.owner_id && <Button asChild size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"><Link to="/billing">Choose a plan</Link></Button>}
        </div>
      )}

      <section className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 space-y-4">
          <div className="flex items-center justify-between"><h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Your clients <span className="text-slate-400 font-medium">({data.clients.length})</span></h2></div>
          <InviteForm kind="client" onDone={onInvited} disabled={inactive} />
        </div>
        <ul className="divide-y divide-slate-100" data-testid="clients-list">
          {data.clients.length === 0 && <li className="px-6 py-10 text-center text-slate-500" data-testid="clients-empty">No clients yet. Invite your first client above.</li>}
          {data.clients.map((c) => <Row key={c.id} u={c} showStats />)}
        </ul>
      </section>

      {data.can_invite_team && (
        <section className="rounded-3xl bg-white border border-slate-200 overflow-hidden" data-testid="team-section">
          <div className="p-6 border-b border-slate-100 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Plus className="w-5 h-5 text-indigo-600" /> Clinicians in your practice</h2>
              <span className="text-sm text-slate-500" data-testid="team-seats">{data.seats.used} of {data.seats.total} seats used</span>
            </div>
            <InviteForm kind="therapist" onDone={onInvited} disabled={inactive || data.seats.used >= data.seats.total} />
          </div>
          <ul className="divide-y divide-slate-100" data-testid="team-list">
            {data.team.length === 0 && <li className="px-6 py-8 text-center text-slate-500">Just you so far. Add clinicians to share your Practice plan.</li>}
            {data.team.map((t) => <Row key={t.id} u={t} />)}
          </ul>
        </section>
      )}
    </div>
  );
}
